'use client'

import React, { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Dropdown } from '@/app/components/common/Dropdown'
import { debug, logError } from '@/lib/logging'
import { Specialties } from '@/types/specialty'

interface SearchWidgetProps {
  onSearchResults?: (results: any[], params?: { specialty?: string; zipCode?: string }) => void
  initialSpecialty?: string
  initialZipCode?: string
  redirectToSearchPage?: boolean
}

export function SearchWidget({
  onSearchResults,
  initialSpecialty,
  initialZipCode,
  redirectToSearchPage = false,
}: SearchWidgetProps) {
  const router = useRouter()
  const [selectedSpecialty, setSelectedSpecialty] = useState(initialSpecialty || 'All Specialties')
  const [zipCode, setZipCode] = useState(initialZipCode || '')
  const [isSearching, setIsSearching] = useState(false)

  const specialties = ['All Specialties', ...Specialties]

  const handleSearch = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSearching(true)

    try {
      debug('ui', `Searching for providers - specialty: ${selectedSpecialty}, zipCode: ${zipCode}`)

      // If redirectToSearchPage is true (landing page), navigate to search page
      if (redirectToSearchPage) {
        const searchParams = new URLSearchParams()
        if (selectedSpecialty !== 'All Specialties') {
          searchParams.append('specialty', selectedSpecialty)
        }
        if (zipCode.trim()) {
          searchParams.append('zipCode', zipCode.trim())
        }

        const searchUrl = `/search${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
        router.push(searchUrl)
        return
      }

      // Otherwise, perform API call (search page)
      const params = new URLSearchParams()
      if (selectedSpecialty !== 'All Specialties') {
        params.append('specialty', selectedSpecialty)
      }
      if (zipCode.trim()) {
        params.append('zipCode', zipCode.trim())
      }

      const response = await fetch(`/api/search/providers?${params.toString()}`)
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to search providers')
      }

      debug('ui', `Found ${data.count} providers`)

      // Call the callback with results if provided
      if (onSearchResults) {
        onSearchResults(data.providers, {
          specialty: selectedSpecialty !== 'All Specialties' ? selectedSpecialty : undefined,
          zipCode: zipCode.trim() || undefined,
        })
      }
    } catch (error) {
      logError('Failed to search providers:', error)
      // Could add error handling here - show toast, etc.
    } finally {
      setIsSearching(false)
    }
  }

  return (
    <form
      data-component-name="SearchWidget"
      className="bg-white flex items-center mx-auto"
      style={{
        borderRadius: '50px',
        border: '1px solid rgb(156, 164, 168)',
        padding: '12px 20px 12px 40px',
        maxWidth: '1100px',
        width: '100%',
        height: '86.25px',
      }}
      onSubmit={handleSearch}
    >
      {/* Specialty Dropdown */}
      <div className="flex items-center">
        <Dropdown
          options={specialties}
          value={selectedSpecialty}
          onChange={setSelectedSpecialty}
          id="specialty-dropdown"
        />
      </div>

      {/* Divider */}
      <div
        className="mx-8"
        style={{
          width: '1px',
          height: '40px',
          backgroundColor: 'rgb(156, 164, 168)',
        }}
      />

      {/* Zip Code Input */}
      <div className="flex items-center flex-1">
        <label
          htmlFor="zipcode"
          className="whitespace-nowrap"
          style={{
            fontSize: '15px',
            fontFamily: 'var(--font-sulsans-light)',
            color: 'rgb(7, 28, 38)',
            marginRight: '10px',
          }}
        >
          Zip code:
        </label>
        <input
          id="zipcode"
          type="text"
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value)}
          className="flex-1"
          style={{
            fontSize: '15px',
            fontFamily: 'var(--font-sulsans-light)',
            color: 'rgb(7, 28, 38)',
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            padding: '0',
            height: '40px',
            width: '197px',
          }}
        />
      </div>

      {/* Find Care Button */}
      <button
        type="submit"
        disabled={isSearching}
        className="text-white"
        style={{
          padding: '20px 40px',
          fontSize: '15px',
          fontFamily: 'var(--font-copernicus)',
          fontWeight: '400',
          backgroundColor: isSearching ? 'rgb(156, 164, 168)' : 'rgb(58, 78, 61)',
          borderRadius: '40px',
          border: 'none',
          cursor: isSearching ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.3s',
          marginLeft: '20px',
        }}
        onMouseEnter={(e) => {
          if (!isSearching) {
            e.currentTarget.style.backgroundColor = 'rgb(47, 63, 49)'
          }
        }}
        onMouseLeave={(e) => {
          if (!isSearching) {
            e.currentTarget.style.backgroundColor = 'rgb(58, 78, 61)'
          }
        }}
      >
        {isSearching ? 'Searching...' : 'Find Care'}
      </button>
    </form>
  )
}
